import { type ExamRoomRequestComputerAttributes } from '@exam-center/graphql';
import { Box, Divider, Flex, Text } from '@shared/components';

import {
  COMPUTER_DETAILS_FEATURES,
  INTERNET_DETAILS_FEATURES,
  SECURITY_DETAILS_FEATURES,
} from '../../containers';
import ReadonlyComputerInfoFeatureCheckRow from '../exam-room-info-details/readonly-computer-info-feature-check-row';
import { ReadonlyFormImagesInput } from '../readonly-form-images-input';

type ComputerDetails = NonNullable<ExamRoomRequestComputerAttributes['computerDetails']>;
type InternetDetails = NonNullable<ExamRoomRequestComputerAttributes['internetDetails']>;
type SecurityDetails = NonNullable<ExamRoomRequestComputerAttributes['securityDetails']>;

interface FeatureData {
  value?: string | null;
  other?: string | null;
}

const computerDetailsAccessors: {
  [key: string]: (cd: ComputerDetails) => FeatureData;
} = {
  sebVersionStatus: (cd) => ({ value: cd.sebVersionStatus }),
  osUpdateCadence: (cd) => ({
    value: cd.osUpdateCadence?.value,
    other: cd.osUpdateCadence?.otherDetail,
  }),
  monitor: (cd) => ({ value: cd.monitor?.value, other: cd.monitor?.otherDetail }),
  cpu: (cd) => ({ value: cd.cpu?.value, other: cd.cpu?.otherDetail }),
  ram: (cd) => ({ value: cd.ram?.value, other: cd.ram?.otherDetail }),
  os: (cd) => ({ value: cd.os?.value, other: cd.os?.otherDetail }),
  storage: (cd) => ({ value: cd.storage?.value, other: cd.storage?.otherDetail }),
  webBrowser: (cd) => ({ value: cd.webBrowser?.value, other: cd.webBrowser?.otherDetail }),
};

const internetDetailsAccessors: {
  [key: string]: (id: InternetDetails) => FeatureData;
} = {
  internetSpeed: (id) => ({
    value: id.internetSpeed?.value,
    other: id.internetSpeed?.otherDetail,
  }),
};

const securityDetailsAccessors: {
  [key: string]: (sd: SecurityDetails) => FeatureData;
} = {
  antivirusProgram: (sd) => ({
    value: sd.antivirusProgram?.value,
    other: sd.antivirusProgram?.otherDetail,
  }),
  antivirusConfigForSEB: (sd) => ({ value: sd.antivirusConfigForSEB }),
  isSyncedWithThaiServer: (sd) => {
    const val = sd.isSyncedWithThaiServer;
    const mappedValue = val == null ? null : val ? 'synced' : 'not-synced';
    return { value: mappedValue };
  },
  usbPortCheck: (sd) => ({ value: sd.usbPortCheck }),
  antivirusUpdateCadence: (sd) => ({
    value: sd.antivirusUpdateCadence?.value,
    other: sd.antivirusUpdateCadence?.otherDetail,
  }),
};

interface ComputerInfoDetailsProps {
  computerDetails: ComputerDetails | undefined | null;
  internetDetails: InternetDetails | undefined | null;
  securityDetails: SecurityDetails | undefined | null;
}

export default function ComputerInfoDetails(props: Readonly<ComputerInfoDetailsProps>) {
  const { computerDetails, internetDetails, securityDetails } = props;

  return (
    <Flex
      sx={(theme) => ({
        flexDirection: 'column',
        gap: theme.spacing(8),
        padding: theme.spacing(8),
      })}
    >
      <Box
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'row',
          gap: theme.spacing(6),
          [theme.breakpoints.down('desktop')]: {
            flexDirection: 'column',
          },
        })}
      >
        <Text variant='body1Semibold' sx={(theme) => ({ width: theme.spacing(60) })}>
          ข้อมูลเกี่ยวกับอุปกรณ์คอมพิวเตอร์
        </Text>
        <Box
          sx={(theme) => ({
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(6),
          })}
        >
          {COMPUTER_DETAILS_FEATURES.map((f) => {
            const accessor = computerDetailsAccessors[f.key];
            const data = computerDetails && accessor ? accessor(computerDetails) : null;

            return (
              <ReadonlyComputerInfoFeatureCheckRow
                key={f.key}
                label={f.label}
                value={data?.value ?? null}
                options={f.options.map((o) => ({ label: o.label, value: String(o.value) }))}
                otherDetails={data?.other ?? null}
                withOthers={f.withOthers}
              />
            );
          })}
          <Flex
            sx={(theme) => ({
              gap: theme.spacing(6),
              justifyContent: 'space-between',
              flexDirection: 'column',
            })}
          >
            <Box sx={{ width: '320px' }}>
              <Text variant='body1' sx={{ fontWeight: 500 }}>
                9.ภาพสเปคคอมพิวเตอร์
              </Text>
            </Box>
            <ReadonlyFormImagesInput images={computerDetails?.computerSpecImages ?? []} />
          </Flex>
          <Flex
            sx={(theme) => ({
              gap: theme.spacing(6),
              justifyContent: 'space-between',
              flexDirection: 'column',
            })}
          >
            <Box sx={{ width: '320px' }}>
              <Text variant='body1' sx={{ fontWeight: 500 }}>
                10.ภาพคอมพิวเตอร์ และที่นั่งสอบ
              </Text>
            </Box>
            <ReadonlyFormImagesInput images={computerDetails?.computerSeatImages ?? []} />
          </Flex>
        </Box>
      </Box>
      <Divider sx={{ my: 2 }} />
      <Box
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'row',
          gap: theme.spacing(6),
          [theme.breakpoints.down('desktop')]: {
            flexDirection: 'column',
          },
        })}
      >
        <Text variant='body1Semibold' sx={(theme) => ({ width: theme.spacing(60) })}>
          ข้อมูลเกี่ยวกับอินเตอร์เน็ต
        </Text>
        <Box
          sx={(theme) => ({
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(6),
          })}
        >
          {INTERNET_DETAILS_FEATURES.map((f) => {
            const accessor = internetDetailsAccessors[f.key];
            const data = internetDetails && accessor ? accessor(internetDetails) : null;

            return (
              <ReadonlyComputerInfoFeatureCheckRow
                key={f.key}
                label={f.label}
                value={data?.value ?? null}
                options={f.options.map((o) => ({ label: o.label, value: String(o.value) }))}
                otherDetails={data?.other ?? null}
                withOthers={f.withOthers}
              />
            );
          })}
          <Flex
            sx={(theme) => ({
              gap: theme.spacing(6),
              justifyContent: 'space-between',
              flexDirection: 'column',
            })}
          >
            <Box sx={{ width: '320px' }}>
              <Text variant='body1' sx={{ fontWeight: 500 }}>
                2.ภาพการทดสอบความเร็วอินเทอร์เน็ตสามารถทดสอบได้ที่ (speedtest.net หรือ fast.com)
              </Text>
            </Box>
            <ReadonlyFormImagesInput images={internetDetails?.speedTestImages ?? []} />
          </Flex>
        </Box>
      </Box>
      <Divider sx={{ my: 2 }} />
      <Box
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'row',
          gap: theme.spacing(6),
          [theme.breakpoints.down('desktop')]: {
            flexDirection: 'column',
          },
        })}
      >
        <Text variant='body1Semibold' sx={(theme) => ({ width: theme.spacing(60) })}>
          ข้อมูลเกี่ยวกับความปลอดภัย
        </Text>
        <Box
          sx={(theme) => ({
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(6),
          })}
        >
          {SECURITY_DETAILS_FEATURES.slice(0, 3).map((f) => {
            const accessor = securityDetailsAccessors[f.key];
            const data = securityDetails && accessor ? accessor(securityDetails) : null;

            return (
              <ReadonlyComputerInfoFeatureCheckRow
                key={f.key}
                label={f.label}
                value={data?.value ?? null}
                options={f.options.map((o) => ({ label: o.label, value: String(o.value) }))}
                otherDetails={data?.other ?? null}
                withOthers={f.withOthers}
              />
            );
          })}
          <Flex
            sx={(theme) => ({
              gap: theme.spacing(6),
              justifyContent: 'space-between',
              flexDirection: 'column',
            })}
          >
            <Box sx={{ width: '320px' }}>
              <Text variant='body1' sx={{ fontWeight: 500 }}>
                4.ภาพเวลาเครื่องในระบบ
              </Text>
            </Box>
            <ReadonlyFormImagesInput images={securityDetails?.timeSyncImages ?? []} />
          </Flex>
          {SECURITY_DETAILS_FEATURES.slice(3).map((f) => {
            const accessor = securityDetailsAccessors[f.key];
            const data = securityDetails && accessor ? accessor(securityDetails) : null;
            return (
              <ReadonlyComputerInfoFeatureCheckRow
                key={f.key}
                label={f.label}
                value={data?.value ?? null}
                options={f.options.map((o) => ({ label: o.label, value: String(o.value) }))}
                otherDetails={data?.other ?? null}
                withOthers={f.withOthers}
              />
            );
          })}
          <Flex
            sx={(theme) => ({
              gap: theme.spacing(6),
              justifyContent: 'space-between',
              flexDirection: 'column',
            })}
          >
            <Box sx={{ width: '320px' }}>
              <Text variant='body1' sx={{ fontWeight: 500 }}>
                7.ภาพการทดสอบ Robot Test
              </Text>
            </Box>
            <ReadonlyFormImagesInput images={securityDetails?.robotTestImages ?? []} />
          </Flex>
        </Box>
      </Box>
    </Flex>
  );
}
